/**
 * Feature: AI-Ready Roofing CRM
 * /features/ai-roofing-crm
 *
 * Positions Rooflyzer as the first CRM built marketing-first for the roofing industry.
 * SoftwareApplication + FAQPage schema. 15-point industry data model.
 */
import { FeatureFaq, FeatureHeroBadge } from '@/components/marketing/FeaturePageSections';

import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Link } from 'react-router-dom';
import { getAuthLink } from '@/lib/links';
import { softwareApplicationSchema, faqPageSchema } from '@/lib/schemas';
import { Helmet } from 'react-helmet-async';
import {
  Brain,
  ArrowRight,
  CheckCircle2,
  CloudLightning,
  MapPin,
  Shield,
  BarChart3,
  Users,
  TrendingUp,
  Star,
  Database,
  Layers,
  GitBranch,
  FileText,
  DollarSign,
  RefreshCw,
  Home,
  Search,
  Award,
} from 'lucide-react';
import { ProductShowcase } from '@/components/marketing/ProductShowcase';
import { PRODUCT_FRAMES } from '@/lib/productFrames';

const faqs = [
  {
    question: 'How is Rooflyzer different from JobNimbus or AccuLynx?',
    answer:
      'Those tools focus on project management and estimating. Rooflyzer is built marketing-first — every feature from lead capture to job close is designed to improve conversion rates and build a data advantage for the future.',
  },
  {
    question: "What does 'AI-ready data' actually mean?",
    answer:
      'It means every data point we collect is structured, labeled, and stored in a format that can feed machine learning models. When we launch AI features, your years of data will already be clean and useful — not just a pile of notes.',
  },
  {
    question: 'Do I need technical knowledge to use these features?',
    answer:
      'No. The AI-ready architecture is completely invisible to you. You just run your business; Rooflyzer handles the data intelligence layer behind the scenes.',
  },
  {
    question: 'Can I import my existing CRM data?',
    answer:
      'Yes. We offer white-glove data migration on the Scale plan, and self-serve CSV import on all plans.',
  },
  {
    question: 'Is this available on the free plan?',
    answer:
      'Core data collection and the 15-point data model is available on all plans. AI insights and advanced forecasting are Scale plan features.',
  },
];

const dataPoints = [
  {
    icon: CloudLightning,
    title: 'Storm Event Correlation',
    desc: 'Automatically links inbound leads to NOAA hail and wind event data by zip code and date',
  },
  {
    icon: Shield,
    title: 'Insurance Claim Stage Tracking',
    desc: '9-stage workflow: Inspection → Adjuster Appointment → Approved → Supplement → ACV Received → RCV Received → Permit → Production → Complete',
  },
  {
    icon: Home,
    title: 'Property Intelligence Layer',
    desc: 'Captures roof age, square footage, pitch, material, and history from satellite + public records',
  },
  {
    icon: MapPin,
    title: 'Territory Heat Maps',
    desc: 'Visualizes lead density, close rates, and rep performance by zip code and neighborhood',
  },
  {
    icon: Users,
    title: 'Canvassing Attribution',
    desc: 'Tracks which door-to-door route, which canvasser, and which day generated each lead',
  },
  {
    icon: TrendingUp,
    title: 'Seasonal Demand Forecasting',
    desc: 'Predicts lead volume surges based on historical storm data and seasonal patterns in your market',
  },
  {
    icon: FileText,
    title: 'HOA Approval Workflows',
    desc: 'Flags properties in HOA zones and triggers approval documentation steps automatically',
  },
  {
    icon: Layers,
    title: 'Material Cost Intelligence',
    desc: 'Tracks shingle brand, color, and manufacturer at the job level for cost analysis',
  },
  {
    icon: DollarSign,
    title: 'Financing Integration Signals',
    desc: 'Logs which leads were offered financing, which accepted, and correlation to close rate',
  },
  {
    icon: GitBranch,
    title: 'Multi-Touch Attribution',
    desc: 'Every lead has a full attribution chain: first touch, assist touches, and close attribution across channels',
  },
  {
    icon: Brain,
    title: 'Re-Engagement Scoring',
    desc: 'AI-ready score that predicts which dormant leads are most likely to re-engage based on property events, season, and prior behavior',
  },
  {
    icon: Award,
    title: 'Crew Performance Metrics',
    desc: 'Links job completion quality scores, callback rates, and customer ratings to specific crews/subs',
  },
  {
    icon: Search,
    title: 'Adjuster & Insurance Company Intelligence',
    desc: 'Tracks which adjusters, insurance carriers, and public adjusters your team has worked with and outcomes',
  },
  {
    icon: RefreshCw,
    title: 'Referral Source Decay Tracking',
    desc: 'Identifies which referral sources are strengthening vs declining over time',
  },
  {
    icon: BarChart3,
    title: 'Permit & Inspection Milestone Logging',
    desc: 'Captures permit pull dates, inspection pass/fail, and re-inspection outcomes for every job',
  },
];

const painPoints = [
  {
    title: "They don't understand storm cycles",
    desc: 'Generic CRMs have no concept of hail events, storm seasonality, or zip code demand spikes.',
  },
  {
    title: 'No insurance workflow',
    desc: 'From adjuster appointments to supplement tracking, insurance claims are multi-step — generic CRMs treat it as a single "deal".',
  },
  {
    title: 'No territory intelligence',
    desc: "Canvassing routes, HOA zones, and competitor saturation maps don't exist in off-the-shelf tools.",
  },
  {
    title: 'Lead sources are shallow',
    desc: "Door knock, digital ad, referral, storm mailer — each has different follow-up sequences; generic CRMs don't know the difference.",
  },
  {
    title: 'No roof-specific data model',
    desc: 'Square footage, pitch, material type, age of roof — these are fields that matter. Generic CRMs call them "custom fields" with no intelligence.',
  },
];

const automationCards = [
  {
    icon: CloudLightning,
    title: 'Storm Trigger Campaigns',
    desc: 'Auto-launch SMS/email sequences when a storm hits your territory',
    color: 'from-cyan-500/10 to-slate-900 border-cyan-500/20',
    iconColor: 'text-cyan-400',
    iconBg: 'bg-cyan-500/10',
  },
  {
    icon: RefreshCw,
    title: 'Re-Nurture Sequences',
    desc: 'Dormant leads get re-engaged at the right time with the right message',
    color: 'from-blue-500/10 to-slate-900 border-blue-500/20',
    iconColor: 'text-blue-400',
    iconBg: 'bg-blue-500/10',
  },
  {
    icon: Star,
    title: 'Post-Install Review Requests',
    desc: 'Automatically request Google Reviews 3 days after job completion',
    color: 'from-emerald-500/10 to-slate-900 border-emerald-500/20',
    iconColor: 'text-emerald-400',
    iconBg: 'bg-emerald-500/10',
  },
  {
    icon: Users,
    title: 'Referral Activation',
    desc: 'Trigger referral ask sequences to happy customers 30 days after close',
    color: 'from-slate-800/80 to-slate-900 border-slate-700',
    iconColor: 'text-slate-300',
    iconBg: 'bg-slate-800',
  },
];

const dataFlowSteps = [
  { label: 'Lead Captured', color: 'bg-slate-600' },
  { label: 'Enrichment', color: 'bg-slate-500' },
  { label: 'Pipeline Stage', color: 'bg-blue-600' },
  { label: 'Job Closed', color: 'bg-emerald-600' },
  { label: 'Analytics', color: 'bg-cyan-600' },
  { label: 'AI Layer', color: 'bg-indigo-600' },
];

export const AIReadyCRMPage = () => {
  const appSchema = softwareApplicationSchema({
    name: 'Rooflyzer OS — AI-Ready Roofing CRM',
    description:
      'The first roofing CRM built marketing-first. Collects 15+ industry-specific data points including storm correlation, insurance claim stages, property intelligence, and territory heat maps. AI-ready from day one.',
    featureList: [
      'Storm event correlation',
      'Insurance claim stage tracking',
      'Property intelligence layer',
      'Territory heat maps',
      'Canvassing attribution',
      'Seasonal demand forecasting',
      'HOA approval workflows',
      'Multi-touch attribution',
      'Re-engagement scoring',
    ],
    url: 'https://rooflyzer.pro/features/ai-roofing-crm',
    offers: { price: '0', description: 'Free tier available — no credit card required' },
  });

  const faqSchema = faqPageSchema(faqs);

  return (
    <>
      <SEOHead
        title="AI-Ready Roofing CRM — Data Built for the Future | Rooflyzer OS"
        description="The first roofing CRM built marketing-first. Collects 15+ industry-specific data points including storm correlation, insurance claim stages, property intelligence, and territory heat maps. AI-ready from day one."
        canonicalUrl="/features/ai-roofing-crm"
        ogImage="/og/ai-agents.png"
        keywords="roofing CRM, AI roofing software, roofing marketing automation, storm tracking CRM, roofing lead management"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs
            items={[
              { name: 'Features', url: '/features' },
              { name: 'AI-Ready Roofing CRM', url: '/features/ai-roofing-crm' },
            ]}
            className="mb-8"
          />

          {/* ── Hero ── */}
          <section className="text-center max-w-4xl mx-auto mb-20">
            <FeatureHeroBadge icon={Brain}>Built Marketing-First</FeatureHeroBadge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
              The Roofing CRM That's Already{' '}
              <span className="text-white underline decoration-slate-600 decoration-2 underline-offset-8">
                Training for Tomorrow
              </span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Rooflyzer doesn't just track your leads — it structures every touchpoint, job, and
              outcome into an AI-ready data layer purpose-built for the roofing industry.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-slate-950 hover:bg-slate-100 font-semibold shadow-sm px-8"
                onClick={() => window.location.assign(getAuthLink('/register'))}
              >
                Start Free
                <ArrowRight className="ml-2 size-5" />
              </Button>
              <Link to="/demo">
                <Button
                  size="lg"
                  variant="ghost"
                  className="border border-white/20 text-white hover:bg-white/10"
                >
                  See How It Works
                </Button>
              </Link>
            </div>
          </section>

          {/* Product film */}
          <section className="mb-24">
            <ProductShowcase frame={PRODUCT_FRAMES.ai} float={false} />
          </section>

          {/* ── Why Generic CRMs Fail Roofers ── */}
          <section className="mb-24 bg-white/5 backdrop-blur-xl rounded-3xl p-8 sm:p-12 border border-white/10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-4">
              Why Generic CRMs Fail Roofers
            </h2>
            <p className="text-white/70 text-center mb-10 max-w-2xl mx-auto">
              HubSpot and Salesforce were built for software sales cycles. Roofing is a completely
              different beast.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {painPoints.map((point, i) => (
                <div
                  key={i}
                  className="flex gap-4 p-5 rounded-2xl bg-red-500/5 border border-red-500/20"
                >
                  <span className="text-red-400 text-xl font-bold flex-shrink-0 mt-0.5">✕</span>
                  <div>
                    <h3 className="text-white font-semibold mb-1">{point.title}</h3>
                    <p className="text-white/70 text-sm leading-relaxed">{point.desc}</p>
                  </div>
                </div>
              ))}
              {/* Sixth card — the solution tease */}
              <div className="flex gap-4 p-5 rounded-2xl border border-slate-700 bg-slate-900/90 md:col-span-2 lg:col-span-1 shadow-sm">
                <CheckCircle2 className="size-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold mb-1">Rooflyzer solves all of this</h3>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    Every workflow, field, and automation is designed around how roofing businesses
                    actually operate — not generic B2B software.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ── 15 Data Points Grid ── */}
          <section className="mb-24">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 border border-slate-700 mb-4 font-mono text-xs">
                <Database className="size-3.5 text-slate-400" />
                <span className="text-slate-300 font-semibold uppercase tracking-wider">
                  Proprietary Data Model
                </span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                15 Industry-Specific Data Points We Collect That Others Don't
              </h2>
              <p className="text-slate-300 max-w-2xl mx-auto">
                Every one of these is automatically structured, labeled, and stored in a format
                ready for analytics and future AI features.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {dataPoints.map((point, i) => (
                <div
                  key={i}
                  className="bg-slate-900/60 rounded-2xl p-6 border border-slate-800 hover:border-slate-700 transition-colors group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center flex-shrink-0 group-hover:border-slate-600 transition-colors">
                      <point.icon className="size-5 text-slate-300" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="font-mono text-xs font-semibold text-slate-400 bg-slate-800 border border-slate-700 px-2 py-0.5 rounded-full">
                          #{i + 1}
                        </span>
                        <h3 className="text-sm font-semibold text-white">{point.title}</h3>
                      </div>
                      <p className="text-slate-400 text-sm leading-relaxed">{point.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ── Data Layer Architecture ── */}
          <section className="mb-24 rounded-3xl overflow-hidden border border-slate-800 bg-slate-900/60">
            <div className="p-8 sm:p-12">
              <div className="text-center mb-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                  The Data Layer That Makes AI Possible
                </h2>
                <p className="text-white/75 max-w-3xl mx-auto leading-relaxed">
                  Every interaction in Rooflyzer is structured data — not just notes in a text
                  field. We segregate residential, commercial, and insurance restoration workflows
                  into separate pipelines with their own data schemas. When AI-powered insights
                  launch, your data is already clean, labeled, and ready.
                </p>
              </div>

              {/* Data Flow Visual */}
              <div className="mb-10">
                <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-0">
                  {dataFlowSteps.map((step, i) => (
                    <div key={i} className="flex items-center">
                      <div
                        className={`${step.color} rounded-xl px-4 py-3 text-white text-sm font-semibold text-center min-w-[110px]`}
                      >
                        {step.label}
                      </div>
                      {i < dataFlowSteps.length - 1 && (
                        <ArrowRight className="size-5 text-white/30 mx-1 hidden sm:block flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-center text-white/50 text-xs mt-4">
                  Every touchpoint flows through this structured pipeline — no free-text fields, no
                  data loss
                </p>
              </div>

              <div className="grid sm:grid-cols-3 gap-6">
                {[
                  {
                    title: 'Structured by Default',
                    desc: "Every field has a type, label, and relationship. Nothing lives in 'notes'.",
                    icon: Database,
                  },
                  {
                    title: 'Segregated Pipelines',
                    desc: 'Residential, commercial, and insurance restoration have distinct schemas.',
                    icon: Layers,
                  },
                  {
                    title: 'AI-Ready From Day 1',
                    desc: 'Your data is already in the format ML models need. No cleanup required.',
                    icon: Brain,
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="bg-slate-900/80 rounded-2xl p-6 border border-slate-800 text-center"
                  >
                    <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center mx-auto mb-4">
                      <item.icon className="size-6 text-slate-300" />
                    </div>
                    <h3 className="text-white font-bold mb-2">{item.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── Marketing Automation ── */}
          <section className="mb-24">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Marketing Automation Built for the Roof Sales Cycle
              </h2>
              <p className="text-slate-300 max-w-2xl mx-auto">
                Not generic drip campaigns — triggers and sequences tuned to how roofing leads
                actually behave.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {automationCards.map((card, i) => (
                <div key={i} className={`rounded-2xl p-6 border bg-gradient-to-br ${card.color}`}>
                  <div
                    className={`w-12 h-12 rounded-xl ${card.iconBg} flex items-center justify-center mb-4`}
                  >
                    <card.icon className={`size-6 ${card.iconColor}`} />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">{card.title}</h3>
                  <p className="text-slate-300 text-sm leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── FAQ ── */}
          <FeatureFaq faqs={faqs} />

          {/* ── Bottom CTA ── */}
          <section className="text-center rounded-3xl p-8 sm:p-12 border border-slate-800 bg-slate-900/80">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 tracking-tight">
              Ready to Build Your Data Advantage?
            </h2>
            <p className="text-slate-300 mb-2 max-w-xl mx-auto">
              Start free. No credit card required.
            </p>
            <p className="text-slate-400 text-sm mb-8 max-w-xl mx-auto">
              Every job you run, every lead you close, and every storm season you survive is
              building a dataset your competitors will never have.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-slate-950 hover:bg-slate-100 font-semibold shadow-sm px-10"
                onClick={() => window.location.assign(getAuthLink('/register'))}
              >
                Get Early Access
                <ArrowRight className="ml-2 size-5" />
              </Button>
              <Link to="/pricing">
                <Button
                  size="lg"
                  variant="ghost"
                  className="border border-slate-700 text-slate-200 hover:bg-slate-800/60"
                >
                  View Pricing
                </Button>
              </Link>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default AIReadyCRMPage;
